import React, { useEffect, useMemo, useState } from 'react';
import { X, Heart } from 'lucide-react';
import { authAPI } from '../../api/auth';
import Button from '../shared/Button';
import toast from 'react-hot-toast';

const MEDICAL_QUESTIONNAIRE_ITEMS = [
  { key: 'q1', label: 'Есть ли у вас заболевание/состояние, при котором врач ограничил физическую активность?' },
  { key: 'q2', label: 'Есть ли у вас болезни сердца, повышенное давление или боли в груди?' },
  { key: 'q3', label: 'Были ли за последний год боли в груди при нагрузке/в покое, головокружения или обмороки?' },
  { key: 'q4', label: 'Проходили ли вы за последний год операцию или госпитализацию, влияющую на тренировки?' },
  { key: 'q5', label: 'Есть ли у вас хронические заболевания, требующие постоянного наблюдения/лечения?' },
  { key: 'q6', label: 'Есть ли травмы/проблемы опорно-двигательного аппарата, которые могут усилиться при нагрузке?' },
  { key: 'q7', label: 'Для женщин: беременность или период до 3 месяцев после родов?' },
  { key: 'q8', label: 'Рекомендовал ли врач за последние 5 лет ограничить физическую активность?' },
  { key: 'q9', label: 'Есть ли иные медицинские ограничения, не указанные выше?' },
  { key: 'q10', label: 'Если вам меньше 18 лет: получали ли вы запрет/ограничение врача на занятия спортом?' },
];

const createInitialFormData = (initialData = {}, prefillData = {}) => {
  const source = initialData || {};
  const prefill = prefillData || {};
  const fallbackFullName = `${String(prefill.first_name || '').trim()} ${String(prefill.last_name || '').trim()}`.trim();
  const nameParts = String(source.full_name || fallbackFullName || '').trim().split(/\s+/).filter(Boolean);
  const parsedFirstName = nameParts[0] || '';
  const parsedLastName = nameParts.slice(1).join(' ');
  const hwMatches = String(source.height_weight || '').match(/\d+/g) || [];
  const parsedHeight = hwMatches[0] || '';
  const parsedWeight = hwMatches[1] || '';
  return {
    first_name: parsedFirstName,
    last_name: parsedLastName,
    gender: source.gender ?? prefill.gender ?? '',
    phone_number: source.phone_number ?? prefill.phone_number ?? '',
    passport_number: source.passport_number ?? '',
    date_of_birth: source.date_of_birth ?? prefill.date_of_birth ?? '',
    height_cm: parsedHeight,
    weight_kg: parsedWeight,
    residence: source.residence ?? prefill.residence ?? '',
    workplace: source.workplace ?? prefill.workplace ?? '',
    marital_status: source.marital_status ?? '',
    previous_sports: source.previous_sports ?? '',
    current_sports: source.current_sports ?? '',
    personal_records: source.personal_records ?? '',
    sport_goals: source.sport_goals ?? '',
    available_inventory: source.available_inventory ?? '',
    activity_level: source.activity_level ?? 'light',
    additional_info: source.additional_info ?? '',
    medical_questionnaire_answers: source.medical_questionnaire_answers || {
      q1: null,
      q2: null,
      q3: null,
      q4: null,
      q5: null,
      q6: null,
      q7: null,
      q8: null,
      q9: null,
      q10: null,
    },
    medical_questionnaire_confirmed: source.medical_questionnaire_confirmed ?? false,
    coach_agreement_accepted: source.coach_agreement_accepted ?? false,
    health_responsibility_accepted: source.health_responsibility_accepted ?? false,
  };
};

const StepTitle = ({ title, subtitle }) => (
  <div className="mb-4">
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    {subtitle ? <p className="text-sm text-gray-500 mt-1">{subtitle}</p> : null}
  </div>
);

const YesNoField = ({ label, value, onChange }) => (
  <div className="rounded-xl border border-gray-200 p-4 bg-white">
    <p className="text-sm font-medium text-gray-800 mb-2">{label}</p>
    <div className="flex gap-2">
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-4 py-2 rounded-lg text-sm ${value === true ? 'bg-[#4897d2] text-white' : 'bg-gray-100 text-gray-700'}`}
      >
        Да
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 py-2 rounded-lg text-sm ${value === false ? 'bg-[#4897d2] text-white' : 'bg-gray-100 text-gray-700'}`}
      >
        Нет
      </button>
    </div>
  </div>
);

const HealthFormModal = ({ isOpen, onClose, onComplete, initialData, prefillData, required = false }) => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(createInitialFormData(initialData, prefillData));

  const steps = useMemo(
    () => [
      { key: 'questionnaire', title: 'Медицинская анкета' },
      { key: 'summary', title: 'Проверка' },
    ],
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    setFormData(createInitialFormData(initialData, prefillData));
    setStep(0);
  }, [isOpen, initialData, prefillData]);

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    if (field === 'phone_number') {
      setFormData((prev) => ({ ...prev, [field]: String(value || '').replace(/\D/g, '').slice(0, 15) }));
      return;
    }
    if (field === 'passport_number') {
      setFormData((prev) => ({ ...prev, [field]: String(value || '').replace(/\D/g, '').slice(0, 20) }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isStepSatisfied = (currentStep) => {
    if (currentStep === 0) {
      const allAnswered = MEDICAL_QUESTIONNAIRE_ITEMS.every(
        (item) => formData.medical_questionnaire_answers?.[item.key] === true || formData.medical_questionnaire_answers?.[item.key] === false,
      );
      if (!allAnswered) return false;
      if (!formData.medical_questionnaire_confirmed) return false;
    }
    return true;
  };

  const validateStep = (currentStep) => {
    if (!isStepSatisfied(currentStep)) {
      if (currentStep === 0) toast.error('Ответьте на все вопросы и подтвердите достоверность анкеты');
      return false;
    }
    return true;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, steps.length - 1));
  };
  const goBack = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allAnswered = MEDICAL_QUESTIONNAIRE_ITEMS.every(
      (item) => formData.medical_questionnaire_answers?.[item.key] === true || formData.medical_questionnaire_answers?.[item.key] === false,
    );
    if (!allAnswered || !formData.medical_questionnaire_confirmed) {
      toast.error('Ответьте на все вопросы и подтвердите достоверность анкеты');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        medical_questionnaire_answers: formData.medical_questionnaire_answers,
        medical_questionnaire_confirmed: Boolean(formData.medical_questionnaire_confirmed),
        additional_info: formData.additional_info || '',
      };
      await authAPI.updateHealthForm(payload);
      if (onComplete) {
        await Promise.resolve(onComplete());
      }
      toast.success('Добро пожаловать в нашу команду');
      onClose();
    } catch (error) {
      toast.error('Ошибка сохранения анкеты');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (step === 0) {
      return (
        <>
          <StepTitle title="Медицинская анкета" subtitle="Ответьте на все вопросы в формате Да/Нет и подтвердите достоверность" />
          <div className="space-y-3">
            <div className="border rounded-lg p-3 bg-gray-50 space-y-3">
              {MEDICAL_QUESTIONNAIRE_ITEMS.map((item) => (
                <YesNoField
                  key={item.key}
                  label={`${item.label} *`}
                  value={formData.medical_questionnaire_answers?.[item.key]}
                  onChange={(v) => handleChange('medical_questionnaire_answers', {
                    ...(formData.medical_questionnaire_answers || {}),
                    [item.key]: v,
                  })}
                />
              ))}
            </div>
            <label className="flex items-start gap-3">
              <input type="checkbox" className="mt-1" checked={formData.medical_questionnaire_confirmed} onChange={(e) => handleChange('medical_questionnaire_confirmed', e.target.checked)} />
              <span className="text-sm text-gray-700">Подтверждаю достоверность ответов медицинской анкеты *</span>
            </label>
            <textarea
              className="w-full border rounded-lg px-3 py-2.5"
              rows="3"
              placeholder="Если нужно, добавьте комментарий от себя"
              value={formData.additional_info}
              onChange={(e) => handleChange('additional_info', e.target.value)}
            />
          </div>
        </>
      );
    }

    return (
      <>
        <StepTitle title="Проверка перед сохранением" />
        <div className="flex flex-col items-center justify-center rounded-xl border border-[#dbe7f4] bg-[#f8fbff] p-4 mb-4">
          <img
            src="/images/Leikin_Team_Logo.png"
            alt="Leikin Team"
            className="w-20 h-20 object-contain mb-2"
          />
          <p className="text-base sm:text-lg font-semibold text-[#1f3f95] text-center">
            Добро пожаловать в нашу команду
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 text-sm">
          <div className="border rounded-lg p-3">Ответов в анкете: <b>{Object.values(formData.medical_questionnaire_answers || {}).filter((v) => v === true || v === false).length}/10</b></div>
          <div className="border rounded-lg p-3">Подтверждение достоверности: <b>{formData.medical_questionnaire_confirmed ? 'Да' : 'Нет'}</b></div>
          <div className="border rounded-lg p-3">Комментарий: <b>{formData.additional_info || '—'}</b></div>
        </div>
      </>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl max-w-3xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative shadow-2xl">
        {!required && (
          <button onClick={onClose} className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 hover:bg-gray-100 rounded-full transition-colors z-10">
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
          </button>
        )}
        <div className="bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-4 sm:p-6 text-white rounded-t-2xl sm:rounded-t-3xl">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Heart className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
            <h2 className="text-xl sm:text-2xl font-bold">Анкета атлета</h2>
          </div>
          <div className="mt-3 flex items-center gap-2">
            {steps.map((item, index) => (
              <div key={item.key} className={`h-2 rounded-full ${index <= step ? 'bg-white w-12' : 'bg-white/40 w-7'}`} />
            ))}
          </div>
          <p className="text-xs sm:text-sm mt-2 text-white/90">Шаг {step + 1} из {steps.length}: {steps[step]?.title}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
          {renderStep()}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button type="button" onClick={goBack} disabled={step === 0 || loading} className="px-5 py-2.5 border rounded-lg text-sm disabled:opacity-50">
              Назад
            </button>
            {step < steps.length - 1 ? (
              <Button type="button" onClick={goNext} className="flex-1" withArrow disabled={loading || !isStepSatisfied(step)}>
                Далее
              </Button>
            ) : (
              <Button type="submit" disabled={loading} className="flex-1" withArrow>
                {loading ? 'Сохранение...' : 'Сохранить анкету'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default HealthFormModal;
